import { WidgetConfig } from "@/services/widgets";
import { canonicalizeConfigMetrics } from "@/services/metrics";
import { WidgetId } from "@/services/widgets/enums";
import { IconName } from "@/services/icons";
import type { DashboardTab } from "@/types/dashboardTabs";
import { DateRangeSelection } from "@/services/data_filters";

export type AiUpdateStatus = "active" | "pending_ai" | "pending_review";

/** What the last editor did. Blank for a touch made before it was recorded. */
export type ModifiedAction =
  | ""
  | "edit"
  | "undo"
  | "redo"
  | "restore"
  | "approve_ai"
  | "reject_ai"
  | "retry_ai"
  | "settings";

export interface CoverInfo {
  coverImage?: string;
  backgroundMode?: string;
  backgroundPositionX?: number;
  backgroundPositionY?: number;
  colorValue?: string;
  resizingValueX?: number;
  resizingValueY?: number;
}

// The fields both variants share. `Dashboard` and `UnreadableDashboard`
// differ only in `config`, and holding the shared fields in one place is
// what stops the two from drifting the day someone adds a new column.
interface BaseDashboardFields {
  pk: number;
  name: string;
  description: string;
  tabs: DashboardTab[];
  previousConfig: Partial<WidgetConfig>[];
  user: number | null;
  createdBy: number | null;
  modifiedBy: number | null;
  createdAt: string;
  /** Saved with every write except a date roll, so a move sends it back as
   * `expectedModifiedAt` to be refused over a write it never showed. */
  modifiedAt: string;
  token: string;
  isPasswordProtected: boolean;
  isShared: boolean;
  isTemplate: boolean;
  hasDismissedPopup: boolean;
  icon: IconName;
  shareAnnotations: boolean;
  sharedDateFilter?: DateRangeSelection;
  dashboardColorScheme: string;
  coverImage?: CoverInfo;
  hideName?: boolean;
  headingSize?: string;
  isAiPowered: boolean;
  aiPrompt?: string;
  aiUpdateStatus: AiUpdateStatus;
  createdByAi: boolean;
  modifiedByAi: boolean;
  /** Absent where a test or an older response left it out. */
  modifiedAction?: ModifiedAction;
  /** What `modifiedBy` did, in the history panel's words. */
  modifiedSummary?: string;
  isFavorite: boolean;
  /** Newer revisions sit above the one on screen, and a write would
   * supersede them. */
  behindNewestRevision?: boolean;
  /** Where an undo from the revision on screen lands; null when it has
   * nowhere to go. Absent unless the read asked for it (`withUndoSeq`), and
   * from list rows and the answers to saves. */
  undoSeq?: number | null;
}

/** A dashboard whose `config` decoded successfully — safe to render and to
 * pass into any mutation that writes back. Mutation payloads should require
 * this rather than `DashboardResponse`, so a caller cannot forget to check
 * for the fallback shape and persist an empty grid over the original row. */
export interface Dashboard extends BaseDashboardFields {
  configReadable: true;
  config: WidgetConfig[];
}

/** A dashboard whose stored `config` could not be parsed. Kept as a
 * placeholder so the page still renders a banner and the row's metadata
 * (name, share settings, favourites) stays editable — but the config field
 * itself is absent, so any attempt to persist it back is a type error. */
export interface UnreadableDashboard extends BaseDashboardFields {
  configReadable: false;
}

/** The union both queries and mutations return. Consumers narrow on
 * `configReadable` before touching `config`, and TypeScript prevents
 * anyone from feeding the unreadable half back into a save. */
export type DashboardResponse = Dashboard | UnreadableDashboard;

/** The one gate every dashboard write keys off.
 *
 * `configReadable: false` means the server's stored config could not be
 * parsed, so anything derived from the rendered grid would overwrite the row
 * we failed to read. `undefined` is writable: it only occurs before the query
 * resolves, and every write path needs a loaded dashboard first. */
export function isConfigWritable(
  dashboard: Pick<DashboardResponse, "configReadable"> | undefined,
): boolean {
  return dashboard?.configReadable !== false;
}

/** Whether the AI owns the config, so the server refuses a write to it.
 *
 * Mirrors the backend lock: `pending_ai` while a run may still write, and
 * `pending_review` while Reject would throw the write away. */
export function isConfigLockedByAi(
  dashboard: Pick<DashboardResponse, "aiUpdateStatus"> | undefined,
): boolean {
  return (
    dashboard?.aiUpdateStatus === "pending_ai" ||
    dashboard?.aiUpdateStatus === "pending_review"
  );
}

/** Wrap a save so an unreadable config rejects instead of persisting.
 *
 * The gate has to sit on the save itself, not only on the button: autosave
 * fires from a timer and from drag handlers, so a disabled control protects
 * nothing. Rejecting (rather than resolving) keeps the orchestrator from
 * marking the dashboard clean, so no edit is silently dropped. */
export function writeGuardedSave<TPayload, TResult>(
  isWritable: () => boolean,
  message: string,
  save: (payload: TPayload) => Promise<TResult>,
): (payload: TPayload) => Promise<TResult> {
  return (payload) =>
    isWritable() ? save(payload) : Promise.reject(new Error(message));
}

/** The raw server response, before decoding. `config` is `unknown`
 * because the wire shape has been observed to arrive as a dict, or a list
 * holding nulls/strings, when a shape-confused write landed
 * (backend/dashboard/config_history.py). Every code path from the
 * network must run through `decodeDashboardResponse` — that is what turns
 * the fallback discriminant on. */
export interface DashboardWire extends BaseDashboardFields {
  config: unknown;
}

interface OldDashboardWidgetConfig extends Omit<WidgetConfig, "widget"> {
  widget: string;
}

function withDisplayDefaults(config: WidgetConfig): WidgetConfig {
  return {
    ...config,
    showWidgetInfoIcons: config.showWidgetInfoIcons ?? true,
    showWidgetActionButtons: config.showWidgetActionButtons ?? true,
  };
}

// Saved metric spellings are resolved here, where a stored config enters the
// app, rather than at each consumer. The MCP tools persist `engagements` while
// the registry, the API and the response keys all use `engagement`, and
// resolving per consumer produced a run of near-identical bugs — a chart that
// rendered while its CSV export came back empty, an ordering sent canonically
// and filtered by the saved plural. Downstream code sees one spelling.
function hydrateWidgetConfig(config: WidgetConfig): WidgetConfig {
  return canonicalizeConfigMetrics(withDisplayDefaults(config));
}

/**
 * Every human-readable message in a DRF error body, in document order.
 *
 * The body is not `{field: [string]}`: `full_details_exception_handler`
 * rewrites each error as `{message, code}` before it leaves the server, and a
 * nested serializer (`tabs`, a `many=True` field) puts one `{}` per valid
 * entry beside the one that failed. So the walk is recursive, stops at the
 * first object that carries a string `message`, and yields nothing for a
 * container with nothing in it.
 */
function collectMessages(value: unknown): string[] {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(collectMessages);
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (typeof record.message === "string") return [record.message];
    return Object.values(record).flatMap(collectMessages);
  }
  return [];
}

/**
 * The same walk, but only where the body looks like something DRF sent.
 *
 * DRF puts every field's errors in a list, so a top-level bare string is not
 * one of them — except under `detail`, which is where DRF itself puts a
 * non-field error and where `full_details_exception_handler` puts an upstream
 * service failure. Harvesting bare strings under *any* key turns every other
 * JSON body into banner text: a gateway answering `{"error": "Bad Gateway",
 * "requestId": "7f3c…"}` put the request id in front of the user beside the
 * message. Below the top level the walk is unrestricted, because that is where
 * the `{message, code}` objects and the `many=True` placeholders live.
 *
 * The one exception to the top-level rule is a whole body that *is* a
 * `{message, code}`. `full_details_exception_handler` replaces `exc.detail`
 * with `get_full_details()`, which for an `APIException` carrying a bare
 * string returns a dict — and DRF sends a dict detail as the body rather than
 * nesting it under `detail`. Both values are then top-level bare strings and
 * neither key is `detail`, so the filter below dropped every refusal that is
 * not a field error: `NotAuthenticated` and `PermissionDenied` from the
 * permission layer, `NotFound("Dashboard not found.")`, `Throttled`, and the
 * wrong share password a public dashboard refuses — the message a viewer has
 * least chance of guessing. Read by the same `message` rule `collectMessages`
 * applies one level down, so the two levels no longer disagree.
 */
function fieldMessages(body: Record<string, unknown>): string[] {
  if (typeof body.message === "string") return [body.message];
  return Object.entries(body).flatMap(([key, value]) =>
    typeof value === "string" && key !== "detail" ? [] : collectMessages(value),
  );
}

/**
 * What the server actually objected to, rather than the status line.
 *
 * `validate_config` names the offending widget — `tabs[0].widgets[2] id='…':
 * date_range.selection: requires both start and end` — and DRF returns that
 * under the field it came from, wrapped as `{message, code}` (see
 * `collectMessages`). Rendering `String(error)` instead gives the user
 * "AxiosError: Request failed with status code 400" and no way to find the
 * widget, which matters most for exactly the config they cannot fix by
 * guessing: one an agent wrote.
 *
 * A body that is not an object (an HTML error page from a proxy, say), or one
 * whose values are bare strings rather than DRF's per-field lists or its own
 * `{message, code}`, is not a field payload and falls back to the error itself
 * rather than being rendered wholesale.
 */
export function saveErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: unknown } } | undefined)
    ?.response?.data;
  if (data && typeof data === "object") {
    const details = fieldMessages(data as Record<string, unknown>);
    if (details.length) {
      return details.join("\n");
    }
  }
  return String(error);
}

// Every path that puts a Dashboard into the ["dashboard", {pk}] cache goes
// through here, not just the query: the mutations `setQueryData` their raw
// responses, so decoding only on read would let the next PATCH or favourite
// toggle put an unparsable config straight back and clear the flag with it.
export function decodeDashboardResponse(raw: DashboardWire): DashboardResponse {
  // A shape-confused backend write can land config as a dict, or as a list
  // holding nulls/strings (see backend/dashboard/config_history.py). Degrade
  // to an empty grid rather than throwing TypeError from .map() — or from
  // `"widget" in config` — into the retry loop.
  //
  // `configReadable: false` is the important half: it strips `config` off
  // the type entirely, so a caller cannot pass this shape into a mutation
  // that overwrites the very row we failed to parse. CustomizeDashboard
  // autosaves, so persisting an empty list would corrupt the row
  // permanently. Consumers narrow before touching `config`.
  const rawConfig: unknown = raw.config;
  const rawEntries = Array.isArray(rawConfig) ? rawConfig : [];
  const configs = rawEntries.filter(
    (entry): entry is WidgetConfig | OldDashboardWidgetConfig =>
      // !Array.isArray matters: `typeof [] === "object"`, and a nested
      // array survives `"widget" in entry` as a legal index check, so it
      // would be spread into a plausible-looking widget object.
      typeof entry === "object" && entry !== null && !Array.isArray(entry),
  );
  const readable =
    Array.isArray(rawConfig) && configs.length === rawEntries.length;
  if (!readable) {
    const { config: _config, ...rest } = raw;
    return { ...rest, configReadable: false };
  }
  const { config: _config, ...rest } = raw;
  return {
    ...rest,
    configReadable: true,
    config: configs.map((config) => {
      if ("widget" in config) {
        const { widget, ...widgetRest } = config;
        return hydrateWidgetConfig({
          ...widgetRest,
          widgetId: widget as WidgetId,
        });
      }
      return hydrateWidgetConfig(config);
    }),
  };
}
